import { gql } from "lib/apolloClient";
import {
  gradingConfigurationFragment,
  gradingCategorySetsFragment,
  cumulativeTermWeightsFragment,
  gradeScaleFragment,
  gradeScaleDetailFragment,
  gradingConfigurationWrapperFragment,
  standardSetWrapperFragment,
  gradingCategorySetsFragmentWithGradeScaleValues,
  ibGradeScaleCategoryFragment,
  ibGradeScaleTermFragment,
} from "GradingSetupV2/modules/GradingSetupV2Fragments";

export const getGlobalCategoriesQuery = gql`
  query getGlobalCategories($id: ID!, $filters: GlobalCategoryFilters) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        globalCategory(filters: $filters) {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const getCourseGradingSettingsAndCategorySetsQuery = gql`
  query getCourseGradingSettingsAndCategorySets(
    $id: ID!
    $filters: GradingConfigurationFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradingMethodology {
          id
          gradingMethodologyTypes
        }
        gradingConfigurations(filters: $filters) {
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
`;

export const getMultiSubjectGradingSettingsAndCategorySetsQuery = gql`
  query getMultiSubjectGradingSettingsAndCategorySets(
    $ids: [ID!]
    $filters: SubjectGradingConfigurationFilters
    $gradingMethodologyFilters: SubjectGradingMethodologyFilters
  ) {
    multiNode(ids: $ids, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingMethodologies(filters: $gradingMethodologyFilters) {
          id
          gradingMethodologyTypes
        }
        gradingConfigurations(filters: $filters) {
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
`;

export const getCourseGradingCategorySetsQuery = gql`
  query getCourseGradingCategorySets(
    $id: ID!
    $filters: GradingConfigurationFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradingConfigurations(filters: $filters) {
          ...GradingCategorySetsFragmentWithGradeScaleValues
        }
      }
    }
  }
  ${gradingCategorySetsFragmentWithGradeScaleValues}
`;

export const getSubjectGradingCategorySetsQuery = gql`
  query getSubjectGradingCategorySetsQuery(
    $id: ID!
    $filters: SubjectGradingConfigurationFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingConfigurations(filters: $filters) {
          ...GradingCategorySetsFragmentWithGradeScaleValues
        }
      }
      __typename
    }
  }
  ${gradingCategorySetsFragmentWithGradeScaleValues}
`;

export const getCourseGradeScalesQuery = gql`
  query getCourseGradeScales(
    $id: ID!
    $filters: GradeScaleFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradeScales(filters: $filters) {
          ...GradeScaleFragment
        }
      }
    }
  }
  ${gradeScaleFragment}
`;

export const getSubjectGradeScalesQuery = gql`
  query getSubjectGradeScales(
    $id: ID!
    $filters: SubjectGradeScalesFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradeScales(filters: $filters) {
          ...GradeScaleFragment
        }
      }
    }
  }
  ${gradeScaleFragment}
`;

export const getLearningCourseStandardSetsQuery = gql`
  query getLearningCourseStandardSets(
    $id: ID!
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        standardsWrapper {
          standardsEdge {
            standardSet {
              id
              title
              standardSetWrapper {
                ...StandardSetWrapperFragment
              }
              gradingConfigurationWrapper {
                ...GradingConfigurationWrapperFragment
              }
            }
          }
        }
      }
    }
  }
  ${standardSetWrapperFragment}
  ${gradingConfigurationWrapperFragment}
`;

export const getSubjectStandardSetsQuery = gql`
  query getSubjectStandardSets(
    $id: ID!
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        name
        plannerElementNodeSet {
          id
          standardSetWrapper {
            ...StandardSetWrapperFragment
          }
          gradingConfigurationWrapper {
            ...GradingConfigurationWrapperFragment
          }
        }
      }
    }
  }
  ${standardSetWrapperFragment}
  ${gradingConfigurationWrapperFragment}
`;

export const getCourseGradingConfigDetailsQuery = gql`
  query getCourseGradingConfigDetails(
    $id: ID!
    $gradingConfigFilters: GradingConfigurationFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradingConfigurations(filters: $gradingConfigFilters) {
          id
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
          ...CumulativeTermWeightsFragment
        }
        gradingMethodology {
          id
          gradingMethodologyTypes
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
  ${cumulativeTermWeightsFragment}
`;

export const getSubjectGradingConfigDetailsQuery = gql`
  query getSubjectGradingConfigDetails(
    $id: ID!
    $gradingConfigFilters: SubjectGradingConfigurationFilters
    $gradingMethodologyFilters: SubjectGradingMethodologyFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingConfigurations(filters: $gradingConfigFilters) {
          id
          ...GradingConfigurationFragment
          ...GradingCategorySetsFragment
          ...CumulativeTermWeightsFragment
        }
        gradingMethodologies(filters: $gradingMethodologyFilters) {
          id
          gradingMethodologyTypes
        }
      }
    }
  }
  ${gradingConfigurationFragment}
  ${gradingCategorySetsFragment}
  ${cumulativeTermWeightsFragment}
`;

export const getCourseGradeScaleDetailsQuery = gql`
  query getCourseGradeScales(
    $id: ID!
    $filters: GradeScaleFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradeScales(filters: $filters) {
          ...GradeScaleFragment
        }
      }
    }
  }
  ${gradeScaleDetailFragment}
`;

export const getSubjectGradeScaleDetailsQuery = gql`
  query getSubjectGradeScaleDetails(
    $id: ID!
    $filters: SubjectGradeScalesFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradeScales(filters: $filters) {
          ...GradeScaleFragment
        }
      }
    }
  }
  ${gradeScaleDetailFragment}
`;

export const getCourseGradeScalesListQuery = gql`
  query getCourseGradeScalesList($id: ID!, $filters: GradeScaleFilters) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradeScales(filters: $filters) {
          id
          templateName
        }
        standardsWrapper {
          standardsEdge {
            standardSet {
              id
              title
              gradeScales {
                id
                templateName
                displayName
              }
            }
          }
        }
      }
    }
  }
`;

export const getSubjectGradeScalesListQuery = gql`
  query getSubjectGradeScalesList(
    $id: ID!
    $filters: SubjectGradeScalesFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradeScales(filters: $filters) {
          id
          templateName
        }
        plannerElementNodeSet {
          id
          standardSetWrapper {
            gradeScales {
              id
              templateName
              displayName
            }
          }
        }
      }
    }
  }
`;

export const getLearningCourseAssociatedLevelsQuery = gql`
  query getLearningCourseAssociatedLevels($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        associatedLevels {
          id
          label
          abbreviation
          reportDisplayName
        }
        subjectLevelMap {
          subject {
            id
          }
          associatedLevels {
            id
            label
            abbreviation
          }
        }
      }
    }
  }
`;

export const getSubjectAssociatedLevelsQuery = gql`
  query getSubjectAssociatedLevels($id: ID!) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        flatVariants(filters: { archivalState: ACTIVE }) {
          level {
            id
            label
            abbreviation
          }
        }
      }
    }
  }
`;

export const getBlueprintGradingSyncStatusQuery = gql`
  query getBlueprintGradingSyncStatus($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      ... on LearningCourse {
        id
        isGradingConfigAllSynced
      }
    }
  }
`;

export const getBlueprintGradingSyncStatusV2Query = gql`
  query getBlueprintGradingSyncStatusV2($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      ... on LearningCourse {
        id
        blueprintCourseSyncStatusV2(filters: { type: GRADING_CONFIG })
      }
    }
  }
`;

export const getGradingConfigurationLockingInfoQuery = gql`
  query getSyncStatus($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      ... on LearningCourse {
        id
        teacherCourseChangeSyncDataV3(
          filters: { teacherCourseIds: [], page: GRADING_PAGE }
        ) {
          id
          resourceType: itemType
          children {
            id
          }
          lockingInfo(filters: { learningCourseId: $id }) {
            isLocked
          }
          isSyncedWithAllTeacherCourseV2(filters: { learningCourseId: $id }) {
            isConflictExists
          }
        }
        lockingInfo {
          pageTypes
        }
      }
    }
  }
`;

export const getSubjectCategoryGradeScaleQuery = gql`
  query getSubjectCategoryGradeScale(
    $id: ID!
    $filters: SubjectGradingConfigurationFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingConfigurations(filters: $filters) {
          ...IBGradeScaleCategoryFragment
        }
      }
    }
  }
  ${ibGradeScaleCategoryFragment}
`;

export const getSubjectTermGradeScaleQuery = gql`
  query getSubjectTermGradeScale(
    $id: ID!
    $filters: SubjectGradeScalesFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradeScales(filters: $filters) {
          ...IBGradeScaleTermFragment
        }
      }
    }
  }
  ${ibGradeScaleTermFragment}
`;
